import React from "react";
import { ArrowUpRight, TrendingUp, ExternalLink } from "lucide-react";
import LiveQuotePanel from "./LiveQuotePanel";
import { COMPANY } from "../data/content";

// Canonical external URLs (client-verified). Kept here so the fallback and
// section-level chrome share one source of truth with LiveQuotePanel.
const TRADINGVIEW_SYMBOL_URL = "https://www.tradingview.com/symbols/OTC-GCAN/";
const OTC_MARKETS_URL = "https://www.otcmarkets.com/stock/GCAN/quote";
const SEC_EDGAR_CIK_URL =
  "https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=0001695473&type=&dateb=&owner=include&count=40";

/**
 * QuoteSection — self-hosted live market data surface for the Home page.
 * No client-side third-party scripts. Data comes from /api/quote/gcan.
 */
export default function QuoteSection() {
  return (
    <section
      id="live-quote"
      className="relative bg-[#14110E] border-t border-[#3A2E22]"
      data-testid="section-quote"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end mb-8">
          <div className="lg:col-span-8">
            <div className="eyebrow text-[#F26A1B] flex items-center gap-3">
              <span className="rule-orange" /> Live Market Data
            </div>
            <h2 className="mt-5 font-display text-3xl lg:text-4xl text-white font-semibold tracking-tight leading-tight">
              {COMPANY.market}:{COMPANY.ticker} — Live Quote
            </h2>
            <p className="mt-4 text-[#F5F2EB] text-sm leading-relaxed max-w-2xl">
              Real-time quote and 90-day price history for Trafalgar International, Inc.
              common stock, sourced server-side from Yahoo Finance. For an interactive
              chart, open the TradingView symbol page below.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <a
              href={TRADINGVIEW_SYMBOL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#4A3D30] hover:border-white text-white text-xs font-mono uppercase tracking-[0.18em] px-4 py-2.5 transition-colors"
              data-testid="section-tv-link"
            >
              Advanced chart on TradingView <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>

        <div className="border border-[#3A2E22] overflow-hidden">
          <LiveQuotePanel renderFallback={(ctx) => <QuoteFallback reason={ctx?.reason} />} />
        </div>

        <p
          className="mt-6 text-xs text-[#8A8075] leading-relaxed max-w-4xl"
          data-testid="quote-delay-disclaimer"
        >
          Market data provided by Yahoo Finance. Quotes may be delayed. For
          informational purposes only — not investment advice.
        </p>
      </div>
    </section>
  );
}

/** Branded fallback when the quote service has no data yet (first-boot edge). */
function QuoteFallback() {
  return (
    <div
      className="w-full flex flex-col md:flex-row items-stretch"
      style={{ minHeight: 420 }}
      data-testid="quote-fallback"
    >
      <div className="md:w-1/2 p-8 lg:p-10 bg-[#1A1512] border-b md:border-b-0 md:border-r border-[#3A2E22] flex flex-col justify-center">
        <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-[#F26A1B]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#F26A1B] animate-pulse" />
          Live quote temporarily unavailable
        </div>
        <div className="mt-6 flex items-baseline gap-4">
          <span className="font-display text-5xl lg:text-6xl text-white font-semibold">
            {COMPANY.ticker}
          </span>
          <span className="font-mono text-sm text-[#A79E93] uppercase tracking-[0.22em]">
            {COMPANY.market}
          </span>
        </div>
        <div className="mt-4 font-mono text-xs text-[#8A8075] uppercase tracking-[0.22em]">
          SEC CIK {COMPANY.cik}
        </div>
        <p className="mt-6 text-sm text-[#F5F2EB] leading-relaxed">
          The live quote could not be loaded from our data provider. Access the
          current quote directly on TradingView or OTC Markets, or review the
          Company's SEC EDGAR filings.
        </p>
      </div>
      <div className="md:w-1/2 p-8 lg:p-10 bg-[#14110E] flex flex-col justify-center gap-3">
        <a
          href={TRADINGVIEW_SYMBOL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-between gap-3 border border-[#F26A1B] btn-gradient rounded-full px-5 py-4 text-sm font-semibold transition-colors"
          data-testid="fallback-tv-link"
        >
          <span className="inline-flex items-center gap-3">
            <TrendingUp className="h-4 w-4" />
            Open OTC:GCAN on TradingView
          </span>
          <ExternalLink className="h-4 w-4" />
        </a>
        <a
          href={OTC_MARKETS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-between gap-3 border border-[#4A3D30] hover:border-white text-white px-5 py-4 text-sm font-semibold transition-colors"
          data-testid="fallback-otc-link"
        >
          <span className="inline-flex items-center gap-3">
            <TrendingUp className="h-4 w-4" />
            View live quote on OTC Markets
          </span>
          <ExternalLink className="h-4 w-4" />
        </a>
        <a
          href={SEC_EDGAR_CIK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-between gap-3 border border-[#4A3D30] hover:border-white text-white px-5 py-4 text-sm font-semibold transition-colors"
          data-testid="fallback-sec-link"
        >
          <span className="inline-flex items-center gap-3">
            <TrendingUp className="h-4 w-4" />
            Review SEC EDGAR filings
          </span>
          <ArrowUpRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}
